import { useCallback, useEffect, useRef, useState } from 'react';
import { CallState } from 'matrix-js-sdk/lib/webrtc/call';
import { fetchWalletBalanceAction } from '../../../../../shared/actions/creditAction';
import { sendJuicyhouseCallEvent } from '../../../../../shared/utils/matrix/matrixReadUtils';
import { JUICYHOUSE_CALL_EVENT } from '../../../../../shared/utils/matrix/matrixRoomUtils';
import {
  estimateSubscriberCreditsRemainingForActiveCall,
  getCallRequestPricingDetails,
  getNewLowBalanceWarningTiers,
  getSecondsUntilNextFlatRateBlockBoundary,
  LOW_BALANCE_CRITICAL_WARNING_MINUTES,
  LOW_BALANCE_WARNING_MINUTES,
  subscriberCanAffordNextFlatRateBlock,
  subscriberCanContinueFlatRateCall,
} from '../utils/callFormatUtils';
import {
  CALL_ENDED_REASON_INSUFFICIENT_CREDITS,
  CALL_EVENT_ACTION,
  CREATOR_NOTIFY_DELAY_MS,
  getCallMediaTypeFromVoiceUi,
  JUICYHOUSE_CALL_EVENT_VERSION,
} from '../constant';

const isSdkCallConnected = (sdkCallState) =>
  sdkCallState === CallState.Connected || sdkCallState === 'connected';

/** Matrix/Synapse rejects float JSON numbers for `com.juicyhouse.call_event`; encode as decimal strings. */
const matrixCallEventNumberString = (n, fractionDigits) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return x.toFixed(fractionDigits).replace(/\.?0+$/, '') || '0';
};

/**
 * Creator: sends com.juicyhouse.call_event `started` on connect; shows warning when a
 * subscriber_low_credits event is received (at 5- and 1-minute remaining tiers).
 * Subscriber: refreshes wallet on connect; sends subscriber_low_credits when estimated remaining time
 * crosses each warning tier; opens purchase-credits modal at 5 and again at 1 minute remaining.
 * Ends the call when the subscriber cannot cover another billing unit — prorated per-second
 * depletion for per-minute calls, or at the next flat-rate block boundary when the wallet
 * cannot cover another session block (unless they top up from the warning modal).
 * Emits {@link CALL_EVENT_ACTION.SUBSCRIBER_INSUFFICIENT_CREDITS_HANGUP} on the room timeline so the creator can end locally
 * if VoIP hangup signalling is flaky.
 */
export const useLowBalanceCreditCallNotify = ({
  dispatch,
  walletBalance,
  walletBalanceLoading,
  client,
  isInitialized,
  roomId,
  isCreator,
  activeCall,
  sdkCallState,
  matrixRoom,
  isVoiceCallUi,
  creatorInfo,
  callDurationSec,
  endCall,
  setShowPurchaseCreditsModal,
  setPurchaseCreditsLowCreditMode,
  skipLowBalanceProcess = false,
}) => {
  const [showCreditsNotify, setShowCreditsNotify] = useState(false);
  const [creatorLowBalanceMinutesRemaining, setCreatorLowBalanceMinutesRemaining] = useState(null);
  const [subscriberLowCreditMinutesRemaining, setSubscriberLowCreditMinutesRemaining] = useState(null);
  const eventSentRef = useRef(false);
  const activeCallIdRef = useRef(null);
  /** Per-call tiers (5 min, 1 min) already handled — subscriber Matrix events. */
  const lowCreditsEventSentTiersRef = useRef(new Set());
  /** Per-call tiers already used to auto-open the subscriber purchase modal. */
  const subscriberLowCreditModalTiersRef = useRef(new Set());
  /** Per-call tiers already shown to the creator via subscriber_low_credits timeline events. */
  const creatorNotifyTiersShownRef = useRef(new Set());
  const notifyTimeoutRef = useRef(null);
  const insufficientCreditsHangupForCallIdRef = useRef(null);
  const insufficientCreditsHangupTimeoutRef = useRef(null);
  /** Matrix insufficient-credits hangup event sent for the current "can't afford next block" stretch. */
  const insufficientCreditsHangupEventSentRef = useRef(false);
  const wasInsufficientForNextBlockRef = useRef(false);
  /** Creator: dedupe timeline handling for subscriber_insufficient_credits_hangup. */
  const creatorInsufficientHangupHandledForCallIdRef = useRef(null);

  const callKind = getCallMediaTypeFromVoiceUi(isVoiceCallUi);

  useEffect(() => {
    activeCallIdRef.current = activeCall?.callId ?? null;
  }, [activeCall?.callId]);

  const lowCreditTierKey = useCallback((callId, tierMinutes) => `${callId}:${tierMinutes}`, []);

  const clearLowCreditWarningTiersForCall = useCallback(
    (callId) => {
      if (!callId) return;
      const prefix = `${callId}:`;
      for (const key of [...lowCreditsEventSentTiersRef.current]) {
        if (key.startsWith(prefix)) lowCreditsEventSentTiersRef.current.delete(key);
      }
      for (const key of [...subscriberLowCreditModalTiersRef.current]) {
        if (key.startsWith(prefix)) subscriberLowCreditModalTiersRef.current.delete(key);
      }
    },
    []
  );

  const scheduleFlatRateInsufficientHangup = useCallback(
    (callId, hangupAfterMs, sendHangupEvent) => {
      if (!endCall || !callId) return;
      if (insufficientCreditsHangupTimeoutRef.current) {
        clearTimeout(insufficientCreditsHangupTimeoutRef.current);
      }
      insufficientCreditsHangupForCallIdRef.current = callId;
      insufficientCreditsHangupTimeoutRef.current = setTimeout(() => {
        insufficientCreditsHangupTimeoutRef.current = null;
        if (sendHangupEvent) sendHangupEvent();
        endCall(CALL_ENDED_REASON_INSUFFICIENT_CREDITS);
      }, Math.max(0, hangupAfterMs));
    },
    [endCall]
  );

  const dismissCreditsNotify = useCallback(() => {
    setShowCreditsNotify(false);
  }, []);

  const scheduleCreditsNotify = useCallback(
    (minutesRemaining, tierMinutes) => {
      if (!isCreator) return;
      const callId = activeCallIdRef.current;
      if (!callId || !Number.isFinite(tierMinutes)) return;
      const tierKey = lowCreditTierKey(String(callId), tierMinutes);
      if (creatorNotifyTiersShownRef.current.has(tierKey)) return;
      creatorNotifyTiersShownRef.current.add(tierKey);

      if (notifyTimeoutRef.current) {
        clearTimeout(notifyTimeoutRef.current);
        notifyTimeoutRef.current = null;
      }
      notifyTimeoutRef.current = setTimeout(() => {
        notifyTimeoutRef.current = null;
        if (Number.isFinite(minutesRemaining)) {
          setCreatorLowBalanceMinutesRemaining(minutesRemaining);
        }
        setShowCreditsNotify(true);
      }, CREATOR_NOTIFY_DELAY_MS);
    },
    [isCreator, lowCreditTierKey]
  );

  const sendStartedEvent = useCallback(async () => {
    if (!isCreator || !client || !isInitialized || !roomId || !activeCall) return;
    if (eventSentRef.current) return;
    eventSentRef.current = true;

    const myId = client.getUserId?.() ?? '';
    const members = matrixRoom?.getJoinedMembers?.() ?? [];
    const participants = [...new Set(members.map((m) => m.userId).filter(Boolean))];
    if (myId && !participants.includes(myId)) participants.push(myId);

    const content = {
      version: JUICYHOUSE_CALL_EVENT_VERSION,
      action: CALL_EVENT_ACTION.STARTED,
      callType: callKind,
      callId: String(activeCall.callId ?? ''),
      initiator: myId,
      participants,
      duration: 0,
      timestamp: Date.now(),
    };

    await sendJuicyhouseCallEvent(client, { isInitialized, roomId, content });
  }, [activeCall, callKind, client, isCreator, isInitialized, matrixRoom, roomId]);

  const sendSubscriberLowCreditsEvent = useCallback(async () => {
    if (isCreator || !client || !isInitialized || !roomId || !activeCall) return;
    const callId = String(activeCall.callId ?? '');
    if (!callId) return;

    const myId = client.getUserId?.() ?? '';
    const credits = Number(walletBalance?.credits);
    if (!Number.isFinite(credits) || walletBalanceLoading) return;

    const est = estimateSubscriberCreditsRemainingForActiveCall(
      creatorInfo,
      callKind,
      credits,
      callDurationSec
    );
    if (!est.ok) return;

    const { minutesRemaining, ratePerMinuteCredits, pricingModel } = est;
    const flatRateWarningTiers =
      pricingModel === 'flat_rate' ? { exclusiveUpperBound: true } : undefined;
    const newTiers = getNewLowBalanceWarningTiers(
      minutesRemaining,
      callId,
      lowCreditsEventSentTiersRef.current,
      flatRateWarningTiers
    );
    if (newTiers.length === 0) return;

    const mr = Math.max(0, minutesRemaining);
    const minutesRemainingForEvent = Number.isFinite(mr)
      ? matrixCallEventNumberString(mr, 4)
      : '0';

    for (const tierMinutes of newTiers) {
      const tierKey = lowCreditTierKey(callId, tierMinutes);
      if (lowCreditsEventSentTiersRef.current.has(tierKey)) continue;

      const content = {
        version: JUICYHOUSE_CALL_EVENT_VERSION,
        action: CALL_EVENT_ACTION.SUBSCRIBER_LOW_CREDITS,
        callType: callKind,
        callId,
        subscriberId: myId,
        credits: matrixCallEventNumberString(credits, 2),
        minutesRemaining: minutesRemainingForEvent,
        ratePerMinuteCredits: matrixCallEventNumberString(ratePerMinuteCredits, 2),
        warningMinutesThreshold: tierMinutes,
        timestamp: Date.now(),
      };

      const ok = await sendJuicyhouseCallEvent(client, { isInitialized, roomId, content });
      if (ok) lowCreditsEventSentTiersRef.current.add(tierKey);
    }
  }, [
    activeCall,
    callDurationSec,
    callKind,
    client,
    creatorInfo,
    isCreator,
    isInitialized,
    lowCreditTierKey,
    roomId,
    walletBalance?.credits,
    walletBalanceLoading,
  ]);

  useEffect(() => {
    if (skipLowBalanceProcess) return;
    if (!isSdkCallConnected(sdkCallState) || !activeCall || activeCall.callHasEnded?.() || isCreator) {
      return;
    }
    dispatch(fetchWalletBalanceAction());
  }, [activeCall, dispatch, isCreator, sdkCallState, skipLowBalanceProcess]);

  useEffect(() => {
    if (skipLowBalanceProcess) return;
    if (!isSdkCallConnected(sdkCallState) || !activeCall || activeCall.callHasEnded?.() || isCreator) {
      return;
    }
    if (walletBalanceLoading) return;
    void sendSubscriberLowCreditsEvent();
  }, [
    activeCall,
    callDurationSec,
    isCreator,
    sdkCallState,
    sendSubscriberLowCreditsEvent,
    skipLowBalanceProcess,
    walletBalance,
    walletBalanceLoading,
  ]);

  useEffect(() => {
    if (skipLowBalanceProcess) return;
    if (!setShowPurchaseCreditsModal || !setPurchaseCreditsLowCreditMode) return;
    if (!isSdkCallConnected(sdkCallState) || !activeCall || activeCall.callHasEnded?.() || isCreator) {
      return;
    }
    if (walletBalanceLoading) return;

    const credits = Number(walletBalance?.credits);
    if (!Number.isFinite(credits)) return;

    const est = estimateSubscriberCreditsRemainingForActiveCall(
      creatorInfo,
      callKind,
      credits,
      callDurationSec
    );
    if (!est.ok) return;

    const { minutesRemaining, pricingModel } = est;
    const callId = String(activeCall.callId ?? '');
    if (!callId) return;

    const flatRateWarningTiers =
      pricingModel === 'flat_rate' ? { exclusiveUpperBound: true } : undefined;
    const newTiers = getNewLowBalanceWarningTiers(
      minutesRemaining,
      callId,
      subscriberLowCreditModalTiersRef.current,
      flatRateWarningTiers
    );
    if (newTiers.length === 0) return;

    for (const tierMinutes of newTiers) {
      subscriberLowCreditModalTiersRef.current.add(lowCreditTierKey(callId, tierMinutes));
    }

    setSubscriberLowCreditMinutesRemaining(minutesRemaining);
    setPurchaseCreditsLowCreditMode(true);
    setShowPurchaseCreditsModal(true);
  }, [
    activeCall,
    callDurationSec,
    callKind,
    creatorInfo,
    isCreator,
    lowCreditTierKey,
    sdkCallState,
    setPurchaseCreditsLowCreditMode,
    setShowPurchaseCreditsModal,
    skipLowBalanceProcess,
    walletBalance?.credits,
    walletBalanceLoading,
  ]);

  /** Subscriber: end call when balance cannot cover the next billing unit (minute or flat-rate block). */
  useEffect(() => {
    if (skipLowBalanceProcess) return;
    if (!endCall) return;
    if (isCreator) return;
    if (!isSdkCallConnected(sdkCallState) || !activeCall || activeCall.callHasEnded?.()) {
      return;
    }
    if (walletBalanceLoading) return;

    const credits = Number(walletBalance?.credits);
    if (!Number.isFinite(credits)) return;

    const pricing = getCallRequestPricingDetails(creatorInfo, callKind);
    if (!pricing.ok) return;

    const est = estimateSubscriberCreditsRemainingForActiveCall(
      creatorInfo,
      callKind,
      credits,
      callDurationSec
    );
    if (!est.ok) return;

    const callId = String(activeCall.callId ?? '');
    if (!callId) return;

    const clearScheduledInsufficientHangup = () => {
      if (insufficientCreditsHangupTimeoutRef.current) {
        clearTimeout(insufficientCreditsHangupTimeoutRef.current);
        insufficientCreditsHangupTimeoutRef.current = null;
      }
      if (insufficientCreditsHangupForCallIdRef.current === callId) {
        insufficientCreditsHangupForCallIdRef.current = null;
      }
    };

    if (pricing.pricingModel === 'flat_rate') {
      const blockCredits =
        est.blockCredits ??
        pricing.flatRateCredits ??
        pricing.sessionCostCredits ??
        0;
      if (!Number.isFinite(blockCredits) || blockCredits <= 0) return;

      const canContinueCall =
        est.canContinueCall ??
        subscriberCanContinueFlatRateCall(
          credits,
          callDurationSec,
          blockCredits,
          pricing.minDurationMins
        );
      const canAffordNextBlock =
        est.canAffordNextBlock ??
        subscriberCanAffordNextFlatRateBlock(
          credits,
          callDurationSec,
          blockCredits,
          pricing.minDurationMins
        );

      if (canAffordNextBlock) {
        if (wasInsufficientForNextBlockRef.current) {
          clearLowCreditWarningTiersForCall(callId);
          wasInsufficientForNextBlockRef.current = false;
        }
        insufficientCreditsHangupEventSentRef.current = false;
        clearScheduledInsufficientHangup();
        return;
      }

      if (!canContinueCall) {
        if (insufficientCreditsHangupForCallIdRef.current === callId) return;
        insufficientCreditsHangupForCallIdRef.current = callId;
        if (!insufficientCreditsHangupEventSentRef.current) {
          insufficientCreditsHangupEventSentRef.current = true;
          void sendJuicyhouseCallEvent(client, {
            isInitialized,
            roomId,
            content: {
              version: JUICYHOUSE_CALL_EVENT_VERSION,
              action: CALL_EVENT_ACTION.SUBSCRIBER_INSUFFICIENT_CREDITS_HANGUP,
              callType: callKind,
              callId,
              timestamp: Date.now(),
            },
          });
        }
        endCall(CALL_ENDED_REASON_INSUFFICIENT_CREDITS);
        return;
      }

      wasInsufficientForNextBlockRef.current = true;

      const hangupAfterMs =
        getSecondsUntilNextFlatRateBlockBoundary(callDurationSec, pricing.minDurationMins) * 1000;
      const sendHangupEvent = () => {
        if (insufficientCreditsHangupEventSentRef.current) return;
        insufficientCreditsHangupEventSentRef.current = true;
        void sendJuicyhouseCallEvent(client, {
          isInitialized,
          roomId,
          content: {
            version: JUICYHOUSE_CALL_EVENT_VERSION,
            action: CALL_EVENT_ACTION.SUBSCRIBER_INSUFFICIENT_CREDITS_HANGUP,
            callType: callKind,
            callId,
            timestamp: Date.now(),
          },
        });
      };
      scheduleFlatRateInsufficientHangup(callId, hangupAfterMs, sendHangupEvent);
      return;
    }

    const { remainingCredits, ratePerMinuteCredits } = est;
    /** One second of burn at session rate; must match prorated estimate (not a full billed minute). */
    const continuationThresholdCredits = ratePerMinuteCredits / 60;

    if (remainingCredits > 0 && remainingCredits >= continuationThresholdCredits) {
      clearScheduledInsufficientHangup();
      return;
    }

    if (insufficientCreditsHangupForCallIdRef.current === callId) return;
    insufficientCreditsHangupForCallIdRef.current = callId;
    void sendJuicyhouseCallEvent(client, {
      isInitialized,
      roomId,
      content: {
        version: JUICYHOUSE_CALL_EVENT_VERSION,
        action: CALL_EVENT_ACTION.SUBSCRIBER_INSUFFICIENT_CREDITS_HANGUP,
        callType: callKind,
        callId,
        timestamp: Date.now(),
      },
    });
    // Show low-credit UI before forcing hangup so users do not see an abrupt end.
    const criticalTierKey = lowCreditTierKey(callId, LOW_BALANCE_CRITICAL_WARNING_MINUTES);
    if (!subscriberLowCreditModalTiersRef.current.has(criticalTierKey)) {
      subscriberLowCreditModalTiersRef.current.add(criticalTierKey);
      setSubscriberLowCreditMinutesRemaining(
        Number.isFinite(est.minutesRemaining) ? est.minutesRemaining : LOW_BALANCE_CRITICAL_WARNING_MINUTES
      );
      setPurchaseCreditsLowCreditMode?.(true);
      setShowPurchaseCreditsModal?.(true);
    }
    if (insufficientCreditsHangupTimeoutRef.current) {
      clearTimeout(insufficientCreditsHangupTimeoutRef.current);
    }
    const hangupAfterMs =
      remainingCredits <= 0
        ? 0
        : Math.max(0, Math.ceil((remainingCredits / ratePerMinuteCredits) * 60 * 1000));
    insufficientCreditsHangupTimeoutRef.current = setTimeout(() => {
      insufficientCreditsHangupTimeoutRef.current = null;
      endCall(CALL_ENDED_REASON_INSUFFICIENT_CREDITS);
    }, hangupAfterMs);
  }, [
    activeCall,
    callDurationSec,
    callKind,
    client,
    creatorInfo,
    endCall,
    isCreator,
    isInitialized,
    clearLowCreditWarningTiersForCall,
    lowCreditTierKey,
    roomId,
    scheduleFlatRateInsufficientHangup,
    sdkCallState,
    setPurchaseCreditsLowCreditMode,
    setShowPurchaseCreditsModal,
    skipLowBalanceProcess,
    walletBalance?.credits,
    walletBalanceLoading,
  ]);

  useEffect(() => {
    eventSentRef.current = false;
    lowCreditsEventSentTiersRef.current = new Set();
    subscriberLowCreditModalTiersRef.current = new Set();
    creatorNotifyTiersShownRef.current = new Set();
    insufficientCreditsHangupForCallIdRef.current = null;
    insufficientCreditsHangupEventSentRef.current = false;
    wasInsufficientForNextBlockRef.current = false;
    creatorInsufficientHangupHandledForCallIdRef.current = null;
    if (insufficientCreditsHangupTimeoutRef.current) {
      clearTimeout(insufficientCreditsHangupTimeoutRef.current);
      insufficientCreditsHangupTimeoutRef.current = null;
    }
    setShowCreditsNotify(false);
    setCreatorLowBalanceMinutesRemaining(null);
    setSubscriberLowCreditMinutesRemaining(null);
    if (notifyTimeoutRef.current) {
      clearTimeout(notifyTimeoutRef.current);
      notifyTimeoutRef.current = null;
    }
  }, [roomId, activeCall?.callId]);

  useEffect(() => {
    if (skipLowBalanceProcess) return;
    if (!isSdkCallConnected(sdkCallState) || !activeCall || activeCall.callHasEnded?.() || !isCreator) {
      return;
    }
    void sendStartedEvent();
  }, [activeCall, isCreator, sdkCallState, sendStartedEvent, skipLowBalanceProcess]);

  useEffect(() => {
    if (skipLowBalanceProcess) return undefined;
    if (!client || !isInitialized || !roomId || !isCreator) return undefined;

    const onTimeline = (event, room, toStartOfTimeline) => {
      if (toStartOfTimeline) return;
      if (!event || room?.roomId !== roomId) return;
      if (event.getType?.() !== JUICYHOUSE_CALL_EVENT) return;

      const content = event.getContent?.() ?? {};
      if (content.version !== JUICYHOUSE_CALL_EVENT_VERSION) return;

      if (content.action === CALL_EVENT_ACTION.SUBSCRIBER_INSUFFICIENT_CREDITS_HANGUP) {
        const incomingCallId = String(content.callId ?? '');
        const currentId = activeCallIdRef.current;
        if (!incomingCallId || !currentId || incomingCallId !== String(currentId)) return;
        if (creatorInsufficientHangupHandledForCallIdRef.current === incomingCallId) return;
        creatorInsufficientHangupHandledForCallIdRef.current = incomingCallId;
        endCall?.(CALL_ENDED_REASON_INSUFFICIENT_CREDITS);
        return;
      }

      if (content.action !== CALL_EVENT_ACTION.SUBSCRIBER_LOW_CREDITS) return;

      const currentId = activeCallIdRef.current;
      if (!currentId || String(content.callId ?? '') !== String(currentId)) return;

      const minutes = Number(content.minutesRemaining);
      const tierMinutes = Number(content.warningMinutesThreshold);
      scheduleCreditsNotify(
        Number.isFinite(minutes) ? minutes : null,
        Number.isFinite(tierMinutes) ? tierMinutes : LOW_BALANCE_WARNING_MINUTES
      );
    };

    client.on?.('Room.timeline', onTimeline);
    return () => {
      client.removeListener?.('Room.timeline', onTimeline);
    };
  }, [client, endCall, isCreator, isInitialized, roomId, scheduleCreditsNotify, skipLowBalanceProcess]);

  useEffect(() => {
    if (!activeCall || activeCall.callHasEnded?.()) {
      if (insufficientCreditsHangupTimeoutRef.current) {
        clearTimeout(insufficientCreditsHangupTimeoutRef.current);
        insufficientCreditsHangupTimeoutRef.current = null;
      }
      if (notifyTimeoutRef.current) {
        clearTimeout(notifyTimeoutRef.current);
        notifyTimeoutRef.current = null;
      }
      setShowCreditsNotify(false);
    }
  }, [activeCall]);

  return {
    showCreditsNotify,
    dismissCreditsNotify,
    creatorLowBalanceMinutesRemaining,
    subscriberLowCreditMinutesRemaining,
  };
};
